import React, { useState } from "react";

const S = {
  wrapper: {
    marginTop: '10px', marginLeft: '0px', border: '3px', padding: '40px',
    borderStyle: 'solid', borderColor: 'grey', fontFamily: "'Georgia', serif",
    color: '#1a1a2e', lineHeight: '1.7',
  },
  h1: {
    fontSize: '2rem', fontWeight: '700', borderBottom: '3px solid #2E75B6',
    paddingBottom: '8px', marginBottom: '24px', color: '#1F3864',
  },
  h2: {
    fontSize: '1.3rem', fontWeight: '700', color: '#2E75B6', marginTop: '36px',
    marginBottom: '12px', borderLeft: '4px solid #2E75B6', paddingLeft: '10px',
  },
  p: { marginBottom: '12px', fontSize: '0.97rem' },
  infoBox: {
    background: '#EBF3FB', border: '2px solid #2E75B6', borderRadius: '6px',
    padding: '16px 20px', margin: '16px 0',
  },
  infoBoxTitle: {
    fontWeight: '700', fontFamily: "'Courier New', monospace", fontSize: '0.88rem',
    color: '#1F3864', marginBottom: '8px',
  },
  infoBoxItem: {
    fontSize: '0.9rem', margin: '4px 0', paddingLeft: '12px', borderLeft: '2px solid #2E75B6',
  },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '0.92rem' },
  th: { background: '#2E75B6', color: '#fff', padding: '10px 14px', textAlign: 'left', fontWeight: '700', fontSize: '0.9rem' },
  tdE: { padding: '10px 14px', borderBottom: '1px solid #ccd9ea', verticalAlign: 'top', background: '#f5f9ff' },
  tdO: { padding: '10px 14px', borderBottom: '1px solid #ccd9ea', verticalAlign: 'top', background: '#fff' },
  tdL: { fontWeight: '600', color: '#1F3864', whiteSpace: 'nowrap', width: '220px' },
  code: { fontFamily: "'Courier New', monospace", fontSize: '0.88rem', whiteSpace: 'pre-wrap' },
  dWrap: {
    margin: '24px 0', background: '#f8fbff', border: '1px solid #ccd9ea',
    borderRadius: '8px', padding: '20px',
  },
  dTitle: {
    fontWeight: '700', fontSize: '0.95rem', color: '#1F3864',
    marginBottom: '12px', textAlign: 'center', letterSpacing: '0.02em',
  },
  dCaption: { fontSize: '0.84rem', color: '#555', textAlign: 'center', marginTop: '10px', fontStyle: 'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width: '220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i % 2 === 0 ? S.tdE : S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i % 2 === 0 ? S.tdE : S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Parabola Anatomy
// ─────────────────────────────────────────────────────────────────
function DiagramAnatomy() {
  const W = 520, H = 280;
  const ox = 220, oy = 220, sc = 52;
  const toX = x => ox + x * sc;
  const toY = y => oy - y * sc;

  // f(x) = x^2 - 2x - 3  → vertex at (1, -4), roots at x=-1 and x=3, y-int at -3
  const a = 1, b = -2, c = -3;
  const f = x => a * x * x + b * x + c;
  const vx = -b / (2 * a);         // vertex x = 1
  const vy = f(vx);                 // vertex y = -4
  const roots = [-1, 3];
  const yInt = c;                   // -3

  const pts = [];
  for (let xi = -2.2; xi <= 4.2; xi += 0.05) {
    const sx = toX(xi), sy = toY(f(xi));
    if (sy > 5 && sy < H - 5) pts.push(`${sx},${sy}`);
  }
  const path = 'M ' + pts.join(' L ');

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Parabola Anatomy — f(x) = x² − 2x − 3</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>
          <defs>
            <marker id="axArrow" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
              <polygon points="0 0,7 3.5,0 7" fill="#1F3864" />
            </marker>
          </defs>

          {/* Grid */}
          {[-2,-1,0,1,2,3,4].map(x => <line key={x} x1={toX(x)} y1={10} x2={toX(x)} y2={H-10} stroke="#eee" strokeWidth="1"/>)}
          {[-4,-3,-2,-1,0,1,2,3].map(y => <line key={y} x1={10} y1={toY(y)} x2={W-10} y2={toY(y)} stroke="#eee" strokeWidth="1"/>)}

          {/* Axis of symmetry */}
          <line x1={toX(vx)} y1={10} x2={toX(vx)} y2={H-10} stroke="#e07b00" strokeWidth="1.3" strokeDasharray="6,4"/>
          <text x={toX(vx)+4} y={18} fontSize="10" fill="#e07b00" fontWeight="700">axis of symmetry</text>
          <text x={toX(vx)+4} y={30} fontSize="10" fill="#e07b00" fontFamily="'Courier New',monospace">x = {vx}</text>

          {/* Axes */}
          <line x1={10} y1={oy} x2={W-10} y2={oy} stroke="#1F3864" strokeWidth="1.5" markerEnd="url(#axArrow)"/>
          <line x1={ox} y1={H-10} x2={ox} y2={10} stroke="#1F3864" strokeWidth="1.5" markerEnd="url(#axArrow)"/>
          <text x={W-8} y={oy-5} fontSize="11" fill="#1F3864" fontWeight="700">x</text>
          <text x={ox+4} y={14} fontSize="11" fill="#1F3864" fontWeight="700">y</text>

          {/* Tick labels */}
          {[-2,-1,1,2,3,4].map(x => <text key={x} x={toX(x)} y={oy+13} textAnchor="middle" fontSize="9" fill="#aaa">{x}</text>)}
          {[-4,-3,-2,-1,1,2,3].map(y => <text key={y} x={ox-5} y={toY(y)+3} textAnchor="end" fontSize="9" fill="#aaa">{y}</text>)}

          {/* Parabola */}
          <path d={path} fill="none" stroke="#2E75B6" strokeWidth="2.5" strokeLinejoin="round"/>

          {/* Roots */}
          {roots.map((r, i) => (
            <g key={i}>
              <circle cx={toX(r)} cy={oy} r={6} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
              <text x={toX(r)} y={oy+22} textAnchor="middle" fontSize="10" fill="#c0392b" fontWeight="700">
                ({r}, 0)
              </text>
              <text x={toX(r)} y={oy+33} textAnchor="middle" fontSize="9" fill="#c0392b">root</text>
            </g>
          ))}

          {/* Y-intercept */}
          <circle cx={ox} cy={toY(yInt)} r={6} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
          <text x={ox+8} y={toY(yInt)-4} fontSize="10" fill="#27ae60" fontWeight="700">(0, {yInt})</text>
          <text x={ox+8} y={toY(yInt)+8} fontSize="9" fill="#27ae60">y-intercept = c</text>

          {/* Vertex */}
          <circle cx={toX(vx)} cy={toY(vy)} r={6} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
          <text x={toX(vx)+8} y={toY(vy)-4} fontSize="10" fill="#e07b00" fontWeight="700">({vx}, {vy})</text>
          <text x={toX(vx)+8} y={toY(vy)+8} fontSize="9" fill="#e07b00">vertex</text>

          {/* Opens upward arrow */}
          <text x={toX(3.6)} y={toY(f(3.6))-8} fontSize="10" fill="#2E75B6" fontWeight="700">opens up</text>
          <text x={toX(3.6)} y={toY(f(3.6))+4} fontSize="9" fill="#888">(a {'>'} 0)</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        The parabola has a vertex (turning point), an axis of symmetry through it, two roots where it crosses x, and a y-intercept equal to c. Every feature maps directly to a, b, and c in the equation.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Effect of 'a' Coefficient
// ─────────────────────────────────────────────────────────────────
function DiagramACoefficient() {
  const W = 520, H = 240;
  const ox = 260, oy = 160, sc = 48;
  const toX = x => ox + x * sc;
  const toY = y => oy - y * sc;

  const cases = [
    { a: -1,  color: '#c0392b',  label: 'a = -1',  note: 'opens down' },
    { a: 0.4, color: '#27ae60',  label: 'a = 0.4', note: 'wide' },
    { a: 1,   color: '#1F3864',  label: 'a = 1',   note: 'standard', bold: true },
    { a: 2.5, color: '#2E75B6',  label: 'a = 2.5', note: 'narrow' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Effect of 'a' — Narrows, Widens, and Flips the Parabola</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>
          {/* Grid */}
          {[-4,-3,-2,-1,0,1,2,3,4].map(x => <line key={x} x1={toX(x)} y1={10} x2={toX(x)} y2={H-10} stroke="#eee" strokeWidth="1"/>)}
          {[-2,-1,0,1,2,3,4].map(y => <line key={y} x1={10} y1={toY(y)} x2={W-10} y2={toY(y)} stroke="#eee" strokeWidth="1"/>)}

          {/* Axes */}
          <line x1={10} y1={oy} x2={W-10} y2={oy} stroke="#1F3864" strokeWidth="1.4"/>
          <line x1={ox} y1={H-10} x2={ox} y2={10} stroke="#1F3864" strokeWidth="1.4"/>
          <text x={W-8} y={oy-4} fontSize="11" fill="#1F3864">x</text>
          <text x={ox+4} y={13} fontSize="11" fill="#1F3864">y</text>
          {[-3,-2,-1,1,2,3].map(x => <text key={x} x={toX(x)} y={oy+13} textAnchor="middle" fontSize="9" fill="#bbb">{x}</text>)}
          {[-2,-1,1,2,3,4].map(y => <text key={y} x={ox-5} y={toY(y)+3} textAnchor="end" fontSize="9" fill="#bbb">{y}</text>)}

          {/* Curves */}
          {cases.map(({ a, color, bold }, ci) => {
            const pts = [];
            for (let xi = -3.5; xi <= 3.5; xi += 0.05) {
              const sy = toY(a * xi * xi);
              if (sy > 5 && sy < H - 5) pts.push(`${toX(xi)},${sy}`);
            }
            return pts.length > 1 ? (
              <path key={ci} d={'M ' + pts.join(' L ')} fill="none" stroke={color}
                strokeWidth={bold ? 3 : 1.8} strokeLinejoin="round" opacity={bold ? 1 : 0.85}/>
            ) : null;
          })}

          {/* Vertex dot */}
          <circle cx={ox} cy={oy} r={5} fill="#1F3864" stroke="#fff" strokeWidth="1.5"/>
          <text x={ox+6} y={oy-7} fontSize="10" fill="#1F3864" fontWeight="700">shared vertex (0,0)</text>

          {/* Legend */}
          <rect x={W-165} y={12} width={158} height={cases.length*22+12} rx={5} fill="#fff" stroke="#ccd9ea"/>
          {cases.map(({ color, label, note, bold }, i) => (
            <g key={i}>
              <line x1={W-158} y1={26+i*22} x2={W-138} y2={26+i*22} stroke={color} strokeWidth={bold?3:1.8}/>
              <text x={W-134} y={30+i*22} fontSize="10" fill={color} fontWeight={bold?'700':'400'} fontFamily="'Courier New',monospace">{label}</text>
              <text x={W-134} y={30+i*22+11} fontSize="9" fill="#888" fontStyle="italic">{note}</text>
            </g>
          ))}
        </svg>
      </div>
      <div style={S.dCaption}>
        All four parabolas share the same vertex at (0, 0). |a| {'>'} 1 narrows the curve. 0 {'<'} |a| {'<'} 1 widens it. Negative a flips it upside down. The sign of a tells you which way it opens.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — The Discriminant (3 cases)
// ─────────────────────────────────────────────────────────────────
function DiagramDiscriminant() {
  const cardW = 155, cardH = 170;
  const pad = { l: 28, r: 12, t: 16, b: 30 };
  const iW = cardW - pad.l - pad.r, iH = cardH - pad.t - pad.b;

  const cases = [
    {
      label: 'Two Real Roots',
      sub: 'b² - 4ac > 0',
      color: '#27ae60',
      a: 1, b: -1, c: -2,  // roots at x=2 and x=-1
      disc: 'disc > 0',
    },
    {
      label: 'One Repeated Root',
      sub: 'b² - 4ac = 0',
      color: '#e07b00',
      a: 1, b: -4, c: 4,   // root at x=2 (double)
      disc: 'disc = 0',
    },
    {
      label: 'No Real Roots',
      sub: 'b² - 4ac < 0',
      color: '#c0392b',
      a: 1, b: 0, c: 2,    // no real roots
      disc: 'disc < 0',
    },
  ];

  const xC = cardW / 2;
  const yC = pad.t + iH * 0.55;
  const sc = 26;
  const toX = x => xC + x * sc;
  const toY = y => yC - y * sc;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Discriminant — b² − 4ac Determines How Many Real Roots Exist</div>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {cases.map(({ label, sub, color, a, b, c, disc }, ci) => {
          const f = x => a*x*x + b*x + c;
          const pts = [];
          for (let xi = -3; xi <= 3; xi += 0.06) {
            const sx = toX(xi), sy = toY(f(xi));
            if (sy > 4 && sy < cardH - 4) pts.push(`${sx},${sy}`);
          }
          const path = pts.length > 1 ? 'M ' + pts.join(' L ') : '';

          // compute roots for markers
          const discriminant = b*b - 4*a*c;
          const rootMarkers = [];
          if (discriminant > 0) {
            rootMarkers.push((-b + Math.sqrt(discriminant)) / (2*a));
            rootMarkers.push((-b - Math.sqrt(discriminant)) / (2*a));
          } else if (discriminant === 0) {
            rootMarkers.push(-b / (2*a));
          }

          return (
            <div key={ci} style={{ flex: '1', minWidth: '145px', background: '#fff', border: `2px solid ${color}`, borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
              <div style={{ fontWeight: '700', fontSize: '0.85rem', color, marginBottom: '2px' }}>{label}</div>
              <div style={{ fontSize: '0.78rem', fontFamily: "'Courier New',monospace", color: '#555', marginBottom: '6px' }}>{sub}</div>
              <svg width={cardW} height={cardH} viewBox={`0 0 ${cardW} ${cardH}`} style={{ maxWidth: '100%' }}>
                {/* x-axis */}
                <line x1={pad.l} y1={yC} x2={cardW-pad.r} y2={yC} stroke="#bbb" strokeWidth="1.3"/>
                {/* y-axis */}
                <line x1={xC} y1={pad.t} x2={xC} y2={pad.t+iH} stroke="#bbb" strokeWidth="1.3"/>
                {/* Curve */}
                <path d={path} fill="none" stroke={color} strokeWidth="2.3" strokeLinejoin="round"/>
                {/* Root markers */}
                {rootMarkers.map((r, i) => (
                  <g key={i}>
                    <circle cx={toX(r)} cy={yC} r={5} fill={color} stroke="#fff" strokeWidth="1.5"/>
                  </g>
                ))}
                {/* Complex roots indicator */}
                {discriminant < 0 && (
                  <text x={xC} y={pad.t+iH+18} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">no x-crossing</text>
                )}
                {discriminant === 0 && (
                  <text x={xC} y={pad.t+iH+18} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">tangent to x-axis</text>
                )}
                {discriminant > 0 && (
                  <text x={xC} y={pad.t+iH+18} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">crosses x-axis twice</text>
                )}
                {/* Disc label */}
                <rect x={4} y={4} width={68} height={18} rx={3} fill={color} opacity="0.15"/>
                <text x={38} y={16} textAnchor="middle" fontSize="10" fill={color} fontWeight="700" fontFamily="'Courier New',monospace">{disc}</text>
              </svg>
            </div>
          );
        })}
      </div>
      <div style={S.dCaption}>
        The discriminant b²-4ac is calculated before solving. It tells you exactly how many real solutions to expect — saving work before you apply the full quadratic formula.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Completing the Square Visually
// ─────────────────────────────────────────────────────────────────
function DiagramCompletingSquare() {
  const W = 520, H = 240;
  // Show x² + 6x geometrically → (x+3)² - 9
  // Big square: (x+3)², missing corner 3x3=9
  const sc = 38;
  const x0 = 60, y0 = 30;
  const xVal = 3.2; // visual x length
  const bHalf = 3;  // b/2 = 3

  const xPx = xVal * sc;
  const bPx = bHalf * sc;
  const total = xPx + bPx;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Completing the Square — The Geometric Meaning</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>

          {/* Main x² square */}
          <rect x={x0} y={y0} width={xPx} height={xPx} fill="#d0e8ff" stroke="#2E75B6" strokeWidth="2"/>
          <text x={x0 + xPx/2} y={y0 + xPx/2 + 5} textAnchor="middle" fontSize="16" fill="#2E75B6" fontWeight="700">x²</text>

          {/* Right rectangle: x * (b/2) */}
          <rect x={x0+xPx} y={y0} width={bPx} height={xPx} fill="#ffd0d0" stroke="#c0392b" strokeWidth="2"/>
          <text x={x0+xPx+bPx/2} y={y0+xPx/2+5} textAnchor="middle" fontSize="13" fill="#c0392b" fontWeight="700">bx/2</text>

          {/* Bottom rectangle: (b/2) * x */}
          <rect x={x0} y={y0+xPx} width={xPx} height={bPx} fill="#ffd0d0" stroke="#c0392b" strokeWidth="2"/>
          <text x={x0+xPx/2} y={y0+xPx+bPx/2+5} textAnchor="middle" fontSize="13" fill="#c0392b" fontWeight="700">bx/2</text>

          {/* Missing corner: (b/2)² */}
          <rect x={x0+xPx} y={y0+xPx} width={bPx} height={bPx} fill="#fff3cd" stroke="#e07b00" strokeWidth="2" strokeDasharray="5,3"/>
          <text x={x0+xPx+bPx/2} y={y0+xPx+bPx/2+5} textAnchor="middle" fontSize="12" fill="#e07b00" fontWeight="700">(b/2)²</text>
          <text x={x0+xPx+bPx/2} y={y0+xPx+bPx/2+17} textAnchor="middle" fontSize="10" fill="#e07b00">add this!</text>

          {/* Dimension labels */}
          <text x={x0+xPx/2} y={y0-8} textAnchor="middle" fontSize="12" fill="#2E75B6" fontWeight="700">x</text>
          <text x={x0+xPx+bPx/2} y={y0-8} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">b/2</text>
          <text x={x0-8} y={y0+xPx/2+5} textAnchor="end" fontSize="12" fill="#2E75B6" fontWeight="700">x</text>
          <text x={x0-8} y={y0+xPx+bPx/2+5} textAnchor="end" fontSize="12" fill="#c0392b" fontWeight="700">b/2</text>

          {/* Total side label */}
          <line x1={x0} y1={y0+total+14} x2={x0+total} y2={y0+total+14} stroke="#1F3864" strokeWidth="1.5"/>
          <line x1={x0} y1={y0+total+9} x2={x0} y2={y0+total+19} stroke="#1F3864" strokeWidth="1.5"/>
          <line x1={x0+total} y1={y0+total+9} x2={x0+total} y2={y0+total+19} stroke="#1F3864" strokeWidth="1.5"/>
          <text x={x0+total/2} y={y0+total+28} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">(x + b/2)</text>

          {/* Algebra steps */}
          <rect x={x0+total+30} y={y0} width={210} height={160} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={x0+total+135} y={y0+20} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">x² + bx = ?</text>
          {[
            ['Step 1:', 'x² + 6x', '#555'],
            ['Step 2:', 'x² + 6x + 9 - 9', '#555'],
            ['       ', '(add & subtract (b/2)²)', '#888'],
            ['Step 3:', '(x + 3)² - 9', '#2E75B6'],
            ['       ', 'vertex form!', '#2E75B6'],
          ].map(([lbl, txt, col], i) => (
            <g key={i}>
              <text x={x0+total+40} y={y0+44+i*22} fontSize="10" fill="#888" fontFamily="'Courier New',monospace">{lbl}</text>
              <text x={x0+total+88} y={y0+44+i*22} fontSize="10" fill={col} fontFamily="'Courier New',monospace">{txt}</text>
            </g>
          ))}
        </svg>
      </div>
      <div style={S.dCaption}>
        Completing the square literally means adding the missing corner piece (b/2)² to make a perfect square. You add and subtract it so the equation stays balanced. The result is vertex form: a(x-h)² + k.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Quadratic Formula Derivation
// ─────────────────────────────────────────────────────────────────
function DiagramFormula() {
  const W = 520, H = 250;

  const steps = [
    { from: 'ax² + bx + c = 0',              note: 'Start: standard form',                     color: '#1F3864' },
    { from: 'ax² + bx = -c',                 note: 'Move constant to right side',               color: '#1F3864' },
    { from: 'x² + (b/a)x = -c/a',            note: 'Divide everything by a',                    color: '#555'   },
    { from: 'x² + (b/a)x + (b/2a)² = -c/a + (b/2a)²', note: 'Add (b/2a)² to both sides',      color: '#c0392b' },
    { from: '(x + b/2a)² = (b²-4ac) / 4a²', note: 'Left side is a perfect square',             color: '#e07b00' },
    { from: 'x + b/2a = ± sqrt(b²-4ac) / 2a', note: 'Take square root of both sides',          color: '#27ae60' },
    { from: 'x = (-b ± sqrt(b²-4ac)) / 2a',  note: 'Solve for x — the quadratic formula!',     color: '#2E75B6' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Quadratic Formula — Step-by-Step Derivation via Completing the Square</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>
          {steps.map(({ from, note, color }, i) => {
            const y = 22 + i * 32;
            const isLast = i === steps.length - 1;
            return (
              <g key={i}>
                {/* Connector line */}
                {i > 0 && <line x1={160} y1={y-20} x2={160} y2={y-8} stroke="#ddd" strokeWidth="1.5"/>}
                {/* Step box */}
                <rect x={10} y={y-14} width={295} height={24} rx={4}
                  fill={isLast ? '#EBF3FB' : '#fff'}
                  stroke={isLast ? '#2E75B6' : '#ddd'}
                  strokeWidth={isLast ? 2 : 1}/>
                <text x={20} y={y+2} fontSize={isLast ? 12 : 11} fill={color}
                  fontFamily="'Courier New',monospace" fontWeight={isLast ? '700' : '400'}>{from}</text>
                {/* Note */}
                <text x={315} y={y+2} fontSize="10" fill="#888" fontStyle="italic">{note}</text>
                {/* Arrow between steps */}
                {i < steps.length - 1 && (
                  <text x={155} y={y+18} textAnchor="middle" fontSize="14" fill="#bbb">↓</text>
                )}
              </g>
            );
          })}

          {/* Discriminant callout */}
          <rect x={10} y={H-42} width={200} height={34} rx={4} fill="#fff3cd" stroke="#e07b00" strokeWidth="1.3"/>
          <text x={110} y={H-25} textAnchor="middle" fontSize="10" fill="#e07b00" fontWeight="700">Discriminant = b² - 4ac</text>
          <text x={110} y={H-12} textAnchor="middle" fontSize="9" fill="#888">the part under the square root</text>

          <rect x={320} y={H-42} width={190} height={34} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.3"/>
          <text x={415} y={H-25} textAnchor="middle" fontSize="10" fill="#2E75B6" fontWeight="700">x = (-b ± sqrt(disc)) / 2a</text>
          <text x={415} y={H-12} textAnchor="middle" fontSize="9" fill="#888">two solutions from ±</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        The quadratic formula is not magic — it is what you get when you complete the square on the general form ax²+bx+c=0. Every step follows from simple algebra.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — ME Applications
// ─────────────────────────────────────────────────────────────────
function DiagramMEApplications() {
  const cardW = 155, cardH = 155;
  const pad = { l: 28, r: 10, t: 16, b: 28 };
  const iW = cardW - pad.l - pad.r, iH = cardH - pad.t - pad.b;

  const apps = [
    {
      title: 'Projectile Motion',
      eq: 'y = v₀t - ½gt²',
      color: '#2E75B6',
      fn: t => 1.8*t - 0.5*0.5*t*t,  // scaled for display
      xMax: 4, yMin: 0, yMax: 2,
      xLabel: 'x', yLabel: 'y',
      note: 'height vs time',
    },
    {
      title: 'Beam Deflection',
      eq: 'y = (w/EI)(Lx²-x³)',
      color: '#c0392b',
      fn: x => 0.6*(3*x*x - x*x*x/1.5),
      xMax: 3, yMin: 0, yMax: 2.5,
      xLabel: 'x', yLabel: 'δ',
      note: 'deflection along beam',
    },
    {
      title: 'Kinetic Energy',
      eq: 'KE = ½mv²',
      color: '#27ae60',
      fn: v => 0.4*v*v,
      xMax: 3, yMin: 0, yMax: 2.2,
      xLabel: 'v', yLabel: 'KE',
      note: 'energy vs velocity',
    },
  ];

  const toX = (v, xMax) => pad.l + (v / xMax) * iW;
  const toY = (y, yMin, yMax) => pad.t + iH - ((y - yMin) / (yMax - yMin)) * iH;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Quadratic Equations in Mechanical Engineering</div>
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {apps.map(({ title, eq, color, fn, xMax, yMin, yMax, xLabel, yLabel, note }, ci) => {
          const pts = [];
          for (let xi = 0; xi <= xMax; xi += 0.06) {
            const y = fn(xi);
            if (y >= yMin && y <= yMax * 1.05) {
              pts.push(`${toX(xi, xMax)},${toY(Math.min(y, yMax), yMin, yMax)}`);
            }
          }
          const path = pts.length > 1 ? 'M ' + pts.join(' L ') : '';

          return (
            <div key={ci} style={{ flex: '1', minWidth: '145px', background: '#fff', border: '1px solid #ccd9ea', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
              <div style={{ fontWeight: '700', fontSize: '0.8rem', color, marginBottom: '2px' }}>{title}</div>
              <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: '4px', fontFamily: "'Courier New',monospace" }}>{eq}</div>
              <svg width={cardW} height={cardH} viewBox={`0 0 ${cardW} ${cardH}`} style={{ maxWidth: '100%' }}>
                {/* Axes */}
                <line x1={pad.l} y1={pad.t} x2={pad.l} y2={pad.t+iH} stroke="#bbb" strokeWidth="1.2"/>
                <line x1={pad.l} y1={pad.t+iH} x2={pad.l+iW} y2={pad.t+iH} stroke="#bbb" strokeWidth="1.2"/>
                <text x={pad.l+iW+3} y={pad.t+iH+3} fontSize="9" fill="#aaa">{xLabel}</text>
                <text x={pad.l-3} y={pad.t+4} textAnchor="end" fontSize="9" fill="#aaa">{yLabel}</text>
                {/* Curve */}
                <path d={path} fill="none" stroke={color} strokeWidth="2.3" strokeLinejoin="round"/>
                {/* Area fill */}
                {pts.length > 1 && (
                  <path d={path + ` L ${toX(xMax, xMax)},${toY(yMin, yMin, yMax)} L ${pad.l},${toY(yMin, yMin, yMax)} Z`}
                    fill={color} fillOpacity="0.08"/>
                )}
                {/* Note */}
                <text x={pad.l + iW/2} y={pad.t+iH+18} textAnchor="middle" fontSize="8.5" fill="#888" fontStyle="italic">{note}</text>
              </svg>
            </div>
          );
        })}
      </div>

      {/* Bottom row — formula application table */}
      <div style={{ marginTop: '16px', background: '#fff', border: '1px solid #ccd9ea', borderRadius: '6px', padding: '14px' }}>
        <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1F3864', marginBottom: '8px' }}>
          Using the Quadratic Formula to Solve Engineering Problems
        </div>
        {[
          ['Projectile range',   'Set y(t) = 0  →  ½gt² - v₀t = 0  →  t = 0 or t = 2v₀/g (time of flight)'],
          ['Max height',         'Find vertex: t_max = v₀/g  →  substitute back for y_max'],
          ['Beam reactions',     'Distributed load → quadratic moment diagram → find zero crossing for max moment'],
          ['Resonance avoidance','Natural frequency ωₙ = sqrt(k/m) — keep operating freq away from this value'],
        ].map(([label, content], i) => (
          <div key={i} style={{ display: 'flex', gap: '12px', padding: '6px 0', borderBottom: i < 3 ? '1px solid #eef' : 'none' }}>
            <div style={{ fontWeight: '600', color: '#2E75B6', minWidth: '150px', fontSize: '0.85rem', fontFamily: "'Courier New',monospace" }}>{label}</div>
            <div style={{ fontSize: '0.85rem', color: '#444', fontFamily: "'Courier New',monospace" }}>{content}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function QuadraticEquations(props) {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Quadratic Equations</h1>

      <p style={S.p}>
        A quadratic equation is any equation of the form ax² + bx + c = 0 where a ≠ 0. Its graph is a parabola — a U-shaped curve that appears constantly in mechanics, dynamics, and design. The three coefficients a, b, and c each control a distinct visual feature of that curve.
      </p>

      <InfoBox
        title="Standard Form:  ax² + bx + c = 0   (a ≠ 0)"
        items={[
          "a = leading coefficient — controls width and direction of opening",
          "b = middle coefficient — controls horizontal shift of the vertex",
          "c = constant — equals the y-intercept (value when x = 0)",
          "Degree 2 means maximum of 2 roots (solutions)",
        ]}
      />

      {/* Anatomy */}
      <h2 style={S.h2}>Parabola Anatomy</h2>
      <DiagramAnatomy />

      <Table2 rows={[
        ["Standard form",      "y = ax² + bx + c"],
        ["Vertex form",        "y = a(x - h)² + k\n(h, k) is the vertex — the turning point of the parabola"],
        ["Vertex location",    "x_vertex = -b / (2a)\ny_vertex = f(x_vertex) = c - b² / (4a)"],
        ["Axis of symmetry",   "x = -b / (2a) — vertical line through the vertex\nParabola is a perfect mirror image on each side"],
        ["Y-intercept",        "Set x = 0 → y = c\nAlways equals the constant term"],
        ["Roots (x-intercepts)","Where the parabola crosses y = 0\nFound using the quadratic formula, factoring, or graphing"],
        ["Opens up vs down",   "a > 0 → opens up (vertex is minimum)\na < 0 → opens down (vertex is maximum)"],
      ]} />

      {/* a coefficient */}
      <h2 style={S.h2}>Effect of the Leading Coefficient a</h2>
      <DiagramACoefficient />

      <Table2 rows={[
        ["|a| > 1",    "Parabola is narrower than y = x²"],
        ["0 < |a| < 1","Parabola is wider than y = x²"],
        ["a > 0",      "Opens upward — vertex is a minimum"],
        ["a < 0",      "Opens downward — vertex is a maximum"],
        ["a = 1",      "Standard parabola y = x² — reference shape"],
      ]} />

      {/* Discriminant */}
      <h2 style={S.h2}>The Discriminant</h2>
      <DiagramDiscriminant />

      <Table2 rows={[
        ["Discriminant",      "D = b² - 4ac\nCalculate this first — it tells you how many real solutions exist"],
        ["D > 0",             "Two distinct real roots\nx = (-b ± sqrt(D)) / 2a gives two different values"],
        ["D = 0",             "One repeated real root (double root)\nx = -b / 2a — parabola is tangent to x-axis"],
        ["D < 0",             "No real roots — two complex conjugate roots\nParabola does not cross the x-axis"],
        ["ME significance",   "D < 0 in a vibration problem means no resonance at real frequencies\nD = 0 means critical damping"],
      ]} />

      {/* Completing the Square */}
      <h2 style={S.h2}>Completing the Square</h2>
      <DiagramCompletingSquare />

      <Table2 rows={[
        ["Purpose",           "Converts standard form → vertex form\nReveals vertex location directly"],
        ["Procedure",         "1. Move c to right side:  x² + (b/a)x = -c/a\n2. Add (b/2a)² to both sides\n3. Factor left side as (x + b/2a)²\n4. Solve for x"],
        ["Example",           "x² + 6x + 5 = 0\nx² + 6x = -5\nx² + 6x + 9 = -5 + 9\n(x + 3)² = 4\nx + 3 = ±2\nx = -1 or x = -5"],
        ["Result",            "Vertex form: y = a(x - h)² + k\nVertex at (h, k) is now obvious"],
      ]} />

      {/* Quadratic Formula */}
      <h2 style={S.h2}>The Quadratic Formula</h2>
      <DiagramFormula />

      <InfoBox
        title="Quadratic Formula:  x = (-b ± sqrt(b² - 4ac)) / 2a"
        items={[
          "Works for any quadratic — use when factoring is not obvious",
          "The ± gives both solutions in one formula",
          "The discriminant b²-4ac under the square root determines solution type",
          "Memorize this: it appears on every engineering exam",
        ]}
      />

      <Table2 rows={[
        ["Quadratic formula",  "x = (-b ± sqrt(b² - 4ac)) / (2a)"],
        ["Factoring method",   "If ax² + bx + c factors as a(x - r₁)(x - r₂) = 0\nthen roots are x = r₁ and x = r₂\nFastest when factors are obvious integers"],
        ["Square root method", "For ax² = d form (no bx term):\nx² = d/a → x = ±sqrt(d/a)"],
        ["Graphical method",   "Plot y = ax² + bx + c and read x-intercepts\nUseful for estimating, checking algebraic answer"],
        ["Vieta's formulas",   "For roots r₁ and r₂:\nr₁ + r₂ = -b/a\nr₁ · r₂ = c/a\nUseful shortcut to check your roots"],
      ]} />

      {/* ME Applications */}
      <h2 style={S.h2}>Mechanical Engineering Applications</h2>
      <DiagramMEApplications />

      <InfoBox
        title="Key Takeaway — Quadratics Are Everywhere in ME"
        items={[
          "Projectile & ballistics: height y(t) = v₀t - ½gt² — set to zero and solve for time of flight",
          "Beam deflection: moment and deflection curves are quadratic along the span",
          "Kinetic energy: KE = ½mv² — quadratic in velocity, linear in mass",
          "Natural frequency: ωₙ = sqrt(k/m) comes from solving the quadratic characteristic equation",
          "Stress concentrations: hoop stress in pressure vessels follows quadratic relationships with geometry",
          "Trajectory optimization: minimize/maximize a quadratic objective function by finding the vertex",
        ]}
      />
    </div>
  );
}